import { useEffect } from "react";
import { noop } from "lodash";

import {
  getManualAssortment as getManualAssortmentLib,
  getPackages as getPackagesLib
} from "../../../../commons/libs/content-service";
import { useCommonAppDispatcher } from "../../store/hooks";
import { useBrands } from "../queries/use-brands";
import useEnv from "../queries/use-env";
import useServiceAvailability from "../queries/use-service-availability";
import { useSettings } from "../queries/use-settings";
import { useWawiConnectionInfo } from "../queries/use-wawi-connection";

import useQuery from "./use-query";

interface Arguments {
  onError?: (error: Error) => void;
}

const useInitCore = ({ onError = noop }: Arguments = {}) => {
  const [dispatch, actions] = useCommonAppDispatcher();

  const serviceAvailability = useServiceAvailability({
    onError
  });

  const env = useEnv({
    enabled: serviceAvailability.isSuccess,
    onError
  });

  const settings = useSettings({
    enabled: env.query.isSuccess,
    onError
  });

  // BCD-7697 Replace `state.core.contentPackages` with `useContentPackages`
  const contentPackages = useQuery({
    queryKey: ["contentPackages"],
    queryFn: getPackagesLib,
    onData: packages => {
      dispatch(actions.core.contentPackagesReceived(packages));
    },
    onError,
    enabled: settings.query.isSuccess
  });

  // BCD-7698 Replace `state.core.manualAssortment` with `useManualAssortment`
  const manualAssortment = useQuery({
    queryKey: ["manualAssortment"],
    queryFn: getManualAssortmentLib,
    onData: manualAssortment => {
      dispatch(actions.core.manualAssortmentReceived(manualAssortment));
    },
    onError,
    enabled: contentPackages.isSuccess
  });

  const wawiConnectionInfo = useWawiConnectionInfo({ enabled: manualAssortment.isSuccess });

  const brands = useBrands({
    onError,
    enabled: wawiConnectionInfo.query.isSuccess
  });

  const isInitCoreDone =
    env.query.isSuccess &&
    settings.query.isSuccess &&
    contentPackages.isSuccess &&
    manualAssortment.isSuccess &&
    wawiConnectionInfo.query.isSuccess &&
    brands.query.isSuccess;

  useEffect(() => {
    if (isInitCoreDone) {
      dispatch(actions.core.initCoreDone());
    }
  }, [isInitCoreDone, dispatch, actions.core]);
};

export default useInitCore;
